import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaMapMarkerAlt, FaInstagram, FaWhatsapp, FaChevronRight } from 'react-icons/fa';
import Logo from '../../assets/logo.png';
import { branchesData } from '../../data/branchesData';

const Header = () => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 25);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/explore', label: 'Explore' },
    { to: '/equipments', label: 'My@Idol' },
    { to: '/packages', label: 'Packages' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/testimonials', label: 'Reviews' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] transition-all duration-300">
      {/* ── 1. ULTRA-COMPACT TOP STATUS BAR ── */}
      <div className="bg-[#090a0d]/95 backdrop-blur-md text-gray-400 text-[10px] py-1 px-4 md:px-10 border-b border-white/5 flex items-center justify-between gap-2 select-none">
        {/* Branch Chips */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5">
          <span className="inline-flex items-center gap-1 text-red-500 font-extrabold uppercase tracking-widest text-[9px] bg-red-600/10 px-2 py-0.5 rounded-full border border-red-500/20 flex-shrink-0">
            <FaMapMarkerAlt className="text-[8px]" /> 3 Branches
          </span>
          <div className="flex items-center gap-2.5 text-[10px] flex-shrink-0">
            {branchesData.map((branch, idx) => (
              <a
                key={branch.id}
                href={branch.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="hover:text-red-400 transition-colors flex items-center gap-1 whitespace-nowrap text-gray-300 hover:underline"
              >
                <span className="text-white/60 font-semibold">{branch.unitNumber}:</span>
                <span>{branch.name}</span>
                {idx < branchesData.length - 1 && <span className="text-gray-700 ml-1.5">•</span>}
              </a>
            ))}
          </div>
        </div>

        {/* Quick Social & Hours */}
        <div className="hidden lg:flex items-center gap-3 text-[10px] text-gray-400">
          <a
            href="https://www.instagram.com/idol_fitness_zone?igsi=MXFnYm40aXR3a2d1Ng=="
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 hover:text-red-400 transition-colors"
          >
            <FaInstagram className="text-red-500 text-[10px]" /> Unit 1
          </a>
          <span className="text-gray-800">|</span>
          <a
            href="https://www.instagram.com/idol_fitnesszone2?utm_source=qr&igsi=enFwMHRpbGxwYmQw"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 hover:text-red-400 transition-colors"
          >
            <FaInstagram className="text-red-500 text-[10px]" /> Unit 2
          </a>
          <span className="text-gray-800">|</span>
          <span className="text-gray-400">🕒 5:00 AM – 10:00 PM</span>
        </div>
      </div>

      {/* ── 2. MAIN FLOATING GLASS NAVIGATION BAR ── */}
      <div
        className={`transition-all duration-300 px-4 md:px-10 ${
          scrolled
            ? 'bg-[#101217]/95 backdrop-blur-xl py-2.5 shadow-2xl border-b border-red-600/30'
            : 'bg-[#14161d]/85 backdrop-blur-lg py-3 border-b border-white/10'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Brand Logo & Compact Wordmark */}
          <NavLink to="/" className="flex items-center gap-2.5 group flex-shrink-0" onClick={() => setMenuOpened(false)}>
            <img
              src={Logo}
              alt="IDOL FITNESS ZONE"
              className="h-9 sm:h-10 w-auto object-contain transition-transform group-hover:scale-105 drop-shadow-[0_2px_8px_rgba(229,27,36,0.3)]"
            />
            <div className="flex flex-col">
              <span className="text-sm sm:text-base font-black tracking-wider uppercase text-white leading-none group-hover:text-red-500 transition-colors">
                IDOL FITNESS <span className="text-red-500">ZONE</span>
              </span>
              <span className="text-[8px] uppercase tracking-[0.3em] text-red-500/80 font-bold mt-0.5">
                3 Units • Coimbatore
              </span>
            </div>
          </NavLink>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center">
            <ul className="flex items-center gap-1 list-none m-0 p-0 bg-[#0c0d12]/80 p-1 rounded-2xl border border-white/10 shadow-inner">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === '/'}
                    className={({ isActive }) =>
                      `px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-xl transition-all duration-200 block ${
                        isActive
                          ? 'text-white bg-red-600 shadow-md shadow-red-600/40 font-extrabold'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`
                    }
                    onClick={() => setMenuOpened(false)}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Action CTAs */}
          <div className="hidden lg:flex items-center gap-2.5">
            <a
              href="https://wa.me/917339504861?text=Hi%20Idol%20Fitness%20Zone,%20I%20would%20like%20to%20know%20more!"
              target="_blank"
              rel="noreferrer"
              className="bg-white/5 hover:bg-green-600/20 text-gray-300 hover:text-green-400 border border-white/15 hover:border-green-500/30 text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-xl transition-all flex items-center gap-1.5"
            >
              <FaWhatsapp className="text-green-400 text-xs" />
            </a>
            <NavLink
              to="/contact"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded-xl shadow-lg shadow-red-600/30 transition-all hover:scale-105"
            >
              Start Your Fitness Journey
            </NavLink>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            className="lg:hidden flex flex-col justify-center items-center gap-1 w-9 h-9 bg-[#181a24] border border-red-500/30 rounded-xl cursor-pointer hover:bg-red-600/20 transition-all z-[1001]"
            onClick={() => setMenuOpened(!menuOpened)}
            aria-label="Toggle navigation"
          >
            <span className={`block h-[2px] w-5 bg-white rounded-full transition-transform duration-300 ${menuOpened ? 'rotate-45 translate-y-1.5' : ''}`} />
            <span className={`block h-[2px] w-5 bg-white rounded-full transition-opacity duration-300 ${menuOpened ? 'opacity-0' : ''}`} />
            <span className={`block h-[2px] w-5 bg-white rounded-full transition-transform duration-300 ${menuOpened ? '-rotate-45 -translate-y-1.5' : ''}`} />
          </button>
        </div>
      </div>

      {/* Backdrop overlay for mobile menu */}
      {menuOpened && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-[998] transition-opacity duration-300"
          onClick={() => setMenuOpened(false)}
        />
      )}

      {/* ── 3. COMPACT MOBILE DRAWER ── */}
      <div
        className={`lg:hidden fixed top-0 right-0 w-80 max-w-[85vw] h-screen bg-[#0e1015]/98 backdrop-blur-2xl p-6 pt-20 border-l border-red-600/30 transition-transform duration-300 z-[999] shadow-2xl flex flex-col justify-between overflow-y-auto ${
          menuOpened ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col gap-5">
          {/* Header info */}
          <div className="flex items-center justify-between pb-4 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <img src={Logo} alt="IDOL FITNESS ZONE" className="h-9 w-auto object-contain" />
              <div>
                <div className="text-xs font-black text-white uppercase tracking-wider">
                  IDOL FITNESS <span className="text-red-500">ZONE</span>
                </div>
                <div className="text-[9px] text-gray-400 font-semibold">3 Coimbatore Units</div>
              </div>
            </div>
          </div>

          {/* Links list */}
          <ul className="flex flex-col gap-1.5 list-none m-0 p-0">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl transition-all ${
                      isActive
                        ? 'text-white bg-red-600 font-extrabold shadow-md shadow-red-600/30'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`
                  }
                  onClick={() => setMenuOpened(false)}
                >
                  <span>{link.label}</span>
                  <FaChevronRight className="text-[9px] opacity-60" />
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Drawer Branch Chips */}
        <div className="pt-5 border-t border-white/10 flex flex-col gap-3">
          <span className="text-[10px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
            <FaMapMarkerAlt /> 3 Gym Locations
          </span>
          <div className="flex flex-col gap-1.5">
            {branchesData.map((b) => (
              <a
                key={b.id}
                href={b.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="bg-white/5 hover:bg-white/10 p-2 rounded-xl border border-white/10 flex items-center justify-between text-[11px] text-gray-200 transition-colors"
              >
                <span className="font-semibold">{b.unitNumber}: {b.name}</span>
                <span className="text-[9px] text-red-400 font-bold underline">Maps ↗</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;